import React, { useState } from 'react';
import classnames from 'classnames';
import { useMemoizedFn, useDeepCompareEffect } from 'ahooks';
import { Checkbox } from 'tea-component';
import GeneralSelectFilter, { FilterItemType } from '@gp/components/GeneralSelectFilter';

import type { IBaseProps } from '@home/types/common';
import type { IRecord } from '@gp/types/global';

import './index.scss';

interface IFilterProps extends IBaseProps {
  selectYM?: {
    yoy?: boolean;
    mom?: boolean;
  };
  selectValues?: {
    project: string[];
    studio: string[];
  };
  options?: FilterItemType[];
  onChange?: (studio: string[], project: string[], ym: string[], handleFilterName: string) => void;
}

const DEFAULT_OPTIONS: FilterItemType[] = [];

const SELECT_YM_CODES_MAPPING: IRecord<string> = {
  yoy: '1',
  mom: '2',
};

const getInitialSelectYM = (selectYM?: {
  yoy?: boolean;
  mom?: boolean;
  [key: string]: boolean | undefined;
}): string[] => {
  if (!selectYM) return [];
  const result: string[] = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const key in selectYM) {
    const value = selectYM[key];
    if (!!value) {
      result.push(SELECT_YM_CODES_MAPPING[key]);
    }
  }
  return result;
};

const getInitialSelectValues = (params?: {
  project: string[];
  studio: string[];
}): { project: string[]; studio: string[] } => {
  const { project, studio } = params || {};
  return { project: project || [], studio: studio || [] };
};

const Filter: React.FC<IFilterProps> = (props) => {
  const {
    style,
    className,
    selectYM: selectYMProps,
    selectValues: selectValuesProps,
    options = DEFAULT_OPTIONS,
    onChange,
  } = props;

  const [selectValues, setSelectValues] = useState<IRecord<string[]>>(() => getInitialSelectValues(selectValuesProps));
  const [selectYM, setSelectYM] = useState<string[]>(() => getInitialSelectYM(selectYMProps));

  const handleSelectFilterChange = useMemoizedFn((payload, handleFilterName) => {
    const { studio, project } = payload || {};
    setSelectValues(payload);
    onChange?.(studio || [], project || [], selectYM, handleFilterName);
  });

  const handleYMChange = useMemoizedFn((value: string[]) => {
    const { studio, project } = selectValues || {};
    setSelectYM(value);
    onChange?.(studio || [], project || [], value, 'ym');
  });

  useDeepCompareEffect(() => {
    setSelectYM(getInitialSelectYM(selectYMProps));
  }, [selectYMProps]);

  useDeepCompareEffect(() => {
    setSelectValues(getInitialSelectValues(selectValuesProps));
  }, [selectValuesProps]);

  return (
    <div style={style} className={classnames('dbg-studio-monthly-report-performance-card-filter', className)}>
      <GeneralSelectFilter options={options} onChange={handleSelectFilterChange} />
      <Checkbox.Group className="dbg-performance-card-filter-ym-selector" value={selectYM} onChange={handleYMChange}>
        <Checkbox name="1">YoY</Checkbox>
        <Checkbox name="2">MoM</Checkbox>
      </Checkbox.Group>
    </div>
  );
};

export default React.memo(Filter);
